/** GEOMETRIA EDITORULUI DE PARCAJ: dreptunghiuri rotite, suprapuneri, distanțe.
 *
 *  Tot ce e aici repetă, în browser, exact convențiile cu care generatorul
 *  așază locurile (`architecture/benzi/geometrie.py`, `stalpi.py`): un loc e
 *  un dreptunghi cu centrul `(cx, cy)`, lățimea `w` DE-A LUNGUL direcției `deg`
 *  (axa rândului) și adâncimea `d` perpendicular pe ea — aceeași așezare pe care
 *  o desenează `plan/glyphs.Bay` cu `translate(cx cy) rotate(deg)`. Stâlpii și
 *  culoarele PMR sunt aceleași dreptunghiuri (`_box` din validator); aleile
 *  sunt o axă cu o lățime.
 *
 *  Suprapunerea se întreabă pe POLIGOANE SIMPLE, nu doar pe convexe: camerele
 *  nivelului (nucleele, adăposturile ALA) pot fi concave. Un vârf înăuntru sau
 *  două laturi care se taie e tot ce poate însemna «se suprapun» pentru două
 *  poligoane simple, iar locul care doar ATINGE un vecin pe muchie nu e o
 *  suprapunere — de asta cel întrebat se strânge cu `strans` înainte.
 */
import type { Pt } from "../types";
import { inInel } from "../tronson/doc";
import type { SolverLane } from "../client";

/** Lățimea de circulație a unei alei fără `w` scris — aceeași ca în desen
 *  (`PlanView`, corpul aleii). */
export const LATIME_ALEE_IMPLICITA = 5;

export type Dreptunghi = { cx: number; cy: number; w: number; d: number; deg: number };

/** Axele unui loc: `u` de-a lungul lui `deg` (lățimea, direcția rândului),
 *  `v` perpendicular (adâncimea, spre alee sau spre perete). */
export function axele(deg: number): { u: Pt; v: Pt } {
  const a = (deg * Math.PI) / 180;
  const c = Math.cos(a), s = Math.sin(a);
  return { u: [c, s], v: [-s, c] };
}

/** Cele patru colțuri, în ordinea desenului; `strans` scade de pe fiecare
 *  latură (pozitiv = mai mic). */
export function inelDreptunghi(r: Dreptunghi, strans = 0): Pt[] {
  const { u, v } = axele(r.deg);
  const hw = Math.max(r.w / 2 - strans, 1e-6), hd = Math.max(r.d / 2 - strans, 1e-6);
  const la = (a: number, b: number): Pt =>
    [r.cx + u[0] * a + v[0] * b, r.cy + u[1] * a + v[1] * b];
  return [la(-hw, -hd), la(hw, -hd), la(hw, hd), la(-hw, hd)];
}

/** Corpul unei alei: dreptunghiul de lățime `w` în jurul axei. Fără capete
 *  rotunjite — generatorul le pune tot drepte. */
export function inelAlee(l: SolverLane): Pt[] {
  const L = Math.hypot(l.x2 - l.x1, l.y2 - l.y1) || 1;
  const deg = (Math.atan2(l.y2 - l.y1, l.x2 - l.x1) * 180) / Math.PI;
  return inelDreptunghi({ cx: (l.x1 + l.x2) / 2, cy: (l.y1 + l.y2) / 2,
                          w: L, d: l.w ?? LATIME_ALEE_IMPLICITA, deg });
}

/** Unghiul axei unei alei, în grade, în `[0, 180)`. */
export function unghiulAleii(l: SolverLane): number {
  const deg = (Math.atan2(l.y2 - l.y1, l.x2 - l.x1) * 180) / Math.PI;
  return ((deg % 180) + 180) % 180;
}

export function distLaSegment(p: Pt, a: Pt, b: Pt): number {
  const dx = b[0] - a[0], dy = b[1] - a[1];
  const L2 = dx * dx + dy * dy || 1;
  const t = Math.max(0, Math.min(1, ((p[0] - a[0]) * dx + (p[1] - a[1]) * dy) / L2));
  return Math.hypot(p[0] - a[0] - t * dx, p[1] - a[1] - t * dy);
}

/** E punctul pe carosabilul aleii (la cel mult `tol` de marginea ei)? Corpul e
 *  DREPTUNGHIUL din `inelAlee`, nu o capsulă: dincolo de capătul aleii nu e
 *  carosabil, deci un loc așezat la capăt, pe jumătate în gol, nu e deservit. */
export function peAlee(p: Pt, l: SolverLane, tol = 0.01): boolean {
  const dx = l.x2 - l.x1, dy = l.y2 - l.y1;
  const L = Math.hypot(dx, dy);
  if (L < 1e-9) return false;
  const t = ((p[0] - l.x1) * dx + (p[1] - l.y1) * dy) / L;
  if (t < -tol || t > L + tol) return false;
  const n = Math.abs((p[0] - l.x1) * dy - (p[1] - l.y1) * dx) / L;
  return n <= (l.w ?? LATIME_ALEE_IMPLICITA) / 2 + tol;
}

const orient = (a: Pt, b: Pt, c: Pt) =>
  (b[0] - a[0]) * (c[1] - a[1]) - (b[1] - a[1]) * (c[0] - a[0]);

/** Segmentele `ab` și `cd` se taie PROPRIU (nu doar se ating într-un capăt
 *  sau stau pe aceeași dreaptă)? */
export function segmenteSeTaie(a: Pt, b: Pt, c: Pt, d: Pt): boolean {
  const o1 = orient(a, b, c), o2 = orient(a, b, d);
  const o3 = orient(c, d, a), o4 = orient(c, d, b);
  return ((o1 > 0 && o2 < 0) || (o1 < 0 && o2 > 0))
      && ((o3 > 0 && o4 < 0) || (o3 < 0 && o4 > 0));
}

export type Cutie = { x0: number; y0: number; x1: number; y1: number };

export function cutia(r: readonly Pt[]): Cutie {
  let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
  for (const [x, y] of r) {
    if (x < x0) x0 = x; if (x > x1) x1 = x;
    if (y < y0) y0 = y; if (y > y1) y1 = y;
  }
  return { x0, y0, x1, y1 };
}

const cutiileSeAting = (a: Cutie, b: Cutie) =>
  a.x0 <= b.x1 && b.x0 <= a.x1 && a.y0 <= b.y1 && b.y0 <= a.y1;

/** Două poligoane simple au interior comun? Un vârf al unuia înăuntrul
 *  celuilalt, sau două laturi care se taie. Cutiile se compară întâi, ca
 *  întrebarea pusă pe sute de obiecte să coste cât o comparație. */
export function seSuprapun(a: readonly Pt[], b: readonly Pt[]): boolean {
  if (a.length < 3 || b.length < 3) return false;
  if (!cutiileSeAting(cutia(a), cutia(b))) return false;
  for (const p of a) if (inInel(p, b)) return true;
  for (const p of b) if (inInel(p, a)) return true;
  for (let i = 0; i < a.length; i++) {
    const a0 = a[i], a1 = a[(i + 1) % a.length];
    for (let j = 0; j < b.length; j++) {
      if (segmenteSeTaie(a0, a1, b[j], b[(j + 1) % b.length])) return true;
    }
  }
  return false;
}

/** Inelul `r` stă ÎNTREG în conturul `contur`: toate vârfurile înăuntru și
 *  nicio latură care să-i taie marginea. */
export function inContur(r: readonly Pt[], contur: readonly Pt[]): boolean {
  if (contur.length < 3) return true;
  for (const p of r) if (!inInel(p, contur)) return false;
  for (let i = 0; i < r.length; i++) {
    const a0 = r[i], a1 = r[(i + 1) % r.length];
    for (let j = 0; j < contur.length; j++) {
      if (segmenteSeTaie(a0, a1, contur[j], contur[(j + 1) % contur.length])) return false;
    }
  }
  return true;
}

/** Segmentul `a → b` tăiat la dreptunghiul `[s0, s1] × [t0, t1]` (Liang–Barsky),
 *  sau `null` dacă nu intră în el. */
export function taieSegmentul(a: Pt, b: Pt, s0: number, s1: number, t0: number, t1: number):
    [Pt, Pt] | null {
  const dx = b[0] - a[0], dy = b[1] - a[1];
  let u0 = 0, u1 = 1;
  const p = [-dx, dx, -dy, dy];
  const q = [a[0] - s0, s1 - a[0], a[1] - t0, t1 - a[1]];
  for (let i = 0; i < 4; i++) {
    if (Math.abs(p[i]) < 1e-12) { if (q[i] < 0) return null; continue; }
    const r = q[i] / p[i];
    if (p[i] < 0) { if (r > u1) return null; if (r > u0) u0 = r; }
    else { if (r < u0) return null; if (r < u1) u1 = r; }
  }
  return [[a[0] + u0 * dx, a[1] + u0 * dy], [a[0] + u1 * dx, a[1] + u1 * dy]];
}
